import { AuthProvider } from "@refinedev/core";
import type { Provider } from "@supabase/supabase-js";
import { supabaseClient } from "./utility/supabaseClient";
import { Sentry } from "./sentry";

/** Params Refine hands to `authProvider.login` (Refine types them as `any`). */
interface LoginParams {
  providerName?: Provider;
  email?: string;
  password?: string;
}

// E2E-only password auth, gated by a build-time env var. Stripped from
// production bundles because import.meta.env.VITE_E2E_AUTH is replaced at
// build time — when unset, the password branch becomes dead code and is
// tree-shaken. Set VITE_E2E_AUTH=1 only in the Playwright dev-server env.
const E2E_PASSWORD_AUTH_ENABLED = import.meta.env.VITE_E2E_AUTH === "1";

const authProvider: AuthProvider = {
  login: async ({ providerName, email, password }: LoginParams) => {
    if (E2E_PASSWORD_AUTH_ENABLED && email && password) {
      try {
        const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password });
        if (error) return { success: false, error };
        if (data?.session) return { success: true, redirectTo: "/" };
        return { success: false, error: { message: "No session returned", name: "AuthFailure" } };
      } catch (error) {
        Sentry.captureException(error);
        return { success: false, error: error as Error };
      }
    }

    // sign in with oauth
    try {
      if (providerName) {
        
        const { data, error } = await supabaseClient.auth.signInWithOAuth({
          provider: providerName,
          options: {

            redirectTo: `${window.location.origin}/auth/callback`,
            queryParams: {
              scopes: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile",
              prompt: "select_account",
            },
          }
        });
        if (error) {
          return {
            success: false,
            error,
          };
        }

        if (data?.url) {
          return {
            success: true,
            redirectTo: "/",
          };
        }
      }

    } catch (error) {
      Sentry.captureException(error);
      return {
        success: false,
        error: error as Error,
      };
    }

    return {
      success: false,
      error: {
        message: "Login failed",
        name: "Invalid email or password",
      },
    };
  },
  logout: async () => {
    const { error } = await supabaseClient.auth.signOut();

    if (error) {
      return {
        success: false,
        error,
      };
    }

    Sentry.setUser(null);
    return {
      success: true,
      redirectTo: "/",
    };
  },
  onError: async (error: unknown) => {
    const httpError = error as { status?: number; statusCode?: number } | null | undefined;
    const status = httpError?.status ?? httpError?.statusCode;
    if (status === 401 || status === 403) {
      await supabaseClient.auth.signOut();
      return { error: error as Error, logout: true, redirectTo: "/login" };
    }
    Sentry.captureException(error);
    return { error: error as Error };
  },
  check: async () => {
    try {
      const { data, error } = await supabaseClient.auth.getUser();
      if (error || !data.user) {
        await supabaseClient.auth.signOut();
        Sentry.setUser(null);
        return {
          authenticated: false,
          error: error ?? { message: "Session expired", name: "Not authenticated" },
          logout: true,
          redirectTo: "/login",
        };
      }
      Sentry.setUser({ email: data.user.email });
    } catch (error) {
      Sentry.captureException(error);
      return {
        authenticated: false,
        error: (error as Error) || { message: "Check failed", name: "Not authenticated" },
        logout: true,
        redirectTo: "/login",
      };
    }

    return {
      authenticated: true,
    };
  },
  getPermissions: async () => {
    const user = await supabaseClient.auth.getUser();

    if (user) {
      return user.data.user?.role;
    }

    return null;
  },
  getIdentity: async () => {
    const { data } = await supabaseClient.auth.getUser();

    if (data?.user) {
      return {
        ...data.user,
        name: data.user.email,
      };
    }

    return null;
  },
};

export default authProvider;